import React, { useState, useContext, useEffect, useMemo } from 'react';
import { CheckCircleOutline, NotInterested, InfoOutlined, Search, Add } from '@mui/icons-material';
import {
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Paper,
} from '@mui/material';
import DataTable from '../../../dataTable';
import { IDataTableColumn } from '../../../dataTable/DataTable/model';
import { CompaniesContext, CompaniesProvider, CompaniesContextType } from './contexts';
import EditCompanyForm from './components/EditCompanyForm';

// Interface for company data
export interface ICompany {
  id: number;
  name: string;
  contact: {
    name: string | null;
    phone: string | null;
    email: string | null;
  };
  address: {
    street: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
  };
  status: boolean;
}

// Create a separate component for the companies content
const CompaniesContent: React.FunctionComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { companies, loading, refreshCompanies } = useContext<CompaniesContextType>(CompaniesContext);
  const [initialDataLoaded, setInitialDataLoaded] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [isNewCompany, setIsNewCompany] = useState<boolean>(false);

  // Load companies only once on component mount
  useEffect(() => {
    if (!initialDataLoaded) {
      refreshCompanies();
      setInitialDataLoaded(true);
    }
  }, [initialDataLoaded, refreshCompanies]);

  // Filter companies based on search text
  const filteredCompanies = useMemo(() => {
    return companies.filter((company) => {
      const searchLower = searchText.toLowerCase();
      return (
        company.name.toLowerCase().includes(searchLower) ||
        company.contact.name?.toLowerCase().includes(searchLower) ||
        company.contact.email?.toLowerCase().includes(searchLower) ||
        company.address.city?.toLowerCase().includes(searchLower) ||
        company.address.state?.toLowerCase().includes(searchLower)
      );
    });
  }, [companies, searchText]);

  // Open edit company dialog
  const handleEditCompany = (company: ICompany, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent row click from triggering if coming from icon click
    }
    setSelectedCompany(company);
    setIsNewCompany(false);
    setEditDialogOpen(true);
  };

  // Open add new company dialog
  const handleAddCompany = () => {
    setSelectedCompany(null);
    setIsNewCompany(true);
    setEditDialogOpen(true);
  };

  // Close edit company dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedCompany(null);
    setIsNewCompany(false);
  };

  // Save updated company details
  const handleSaveCompany = async (updatedCompany: ICompany) => {
    try {
      // If it's a new company, a different API call will be made from the EditCompanyForm component
      // Here we only need to refresh the companies list
      await refreshCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
    }
  };

  // Define table columns
  const columns: IDataTableColumn[] = [
    {
      name: 'Name',
      selector: (row: ICompany) => row.name,
      sortable: true,
      wrap: true,
      grow: 1,
    },
    {
      name: 'Contact Name',
      selector: (row: ICompany) => row.contact.name || 'N/A',
      sortable: true,
      wrap: true,
      grow: 1,
    },
    {
      name: 'Email',
      selector: (row: ICompany) => row.contact.email || 'N/A',
      sortable: true,
      wrap: true,
      grow: 1,
    },
    {
      name: 'Phone',
      selector: (row: ICompany) => row.contact.phone || 'N/A',
      sortable: true,
      wrap: true,
      grow: 1,
    },
    {
      name: 'Location',
      selector: (row: ICompany) => {
        const city = row.address.city || '';
        const state = row.address.state || '';
        const location = [city, state].filter(Boolean).join(', ') || 'N/A';

        return <div title={location}>{location}</div>;
      },
      sortable: true,
      wrap: true,
    },
    {
      name: 'Status',
      selector: (row: ICompany) => row.status,
      sortable: true,
      cell: (row: ICompany) => (
        <Tooltip title={row.status ? 'Active' : 'Inactive'} arrow>
          <div>{row.status ? <CheckCircleOutline color="success" /> : <NotInterested color="disabled" />}</div>
        </Tooltip>
      ),
      width: '80px',
      center: true,
    },
  ];

  // Use base columns array directly
  const finalColumns = columns;

  // Adjust columns based on screen size
  const responsiveColumns = isMobile
    ? columns.filter(
        (col) =>
          // Keep only essential columns for mobile view
          col.name === 'Name' || col.name === 'Status',
      )
    : finalColumns;

  return (
    <>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'stretch' : 'center'}
        spacing={isMobile ? 1 : 2}
        mb={isMobile ? 1 : 2}
      >
        <Box>
          <Typography variant="h1" component="h1" m={0} fontSize={isMobile ? '1.5rem' : 'inherit'}>
            Companies Management
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              mt: 1,
              mb: 1,
              color: theme.palette.text.secondary,
              flexWrap: 'wrap',
              fontSize: isMobile ? '0.8rem' : 'inherit',
            }}
          >
            <InfoOutlined fontSize="small" sx={{ mr: 1, mt: '2px' }} />
            <Typography variant="body2" fontSize="inherit">
              Click on a row to edit company details.
            </Typography>
          </Box>
        </Box>

        <Stack
          direction={isMobile ? 'column' : 'row'}
          alignItems="center"
          spacing={isMobile ? 1 : 2}
          width={isMobile ? '100%' : 'auto'}
          mt={isMobile ? 1 : 0}
        >
          <Paper
            elevation={1}
            sx={{
              p: '2px 4px',
              display: 'flex',
              alignItems: 'center',
              width: isMobile ? '100%' : '300px',
              borderRadius: '4px',
            }}
          >
            <TextField
              placeholder="Search companies..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" fontSize="small" />
                  </InputAdornment>
                ),
                sx: { borderRadius: '4px' },
              }}
            />
          </Paper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleAddCompany}
            sx={{ height: 36, whiteSpace: 'nowrap' }}
          >
            Add Company
          </Button>
        </Stack>
      </Stack>
      <Box position="relative" sx={{ overflowX: 'auto' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <DataTable
          columns={isMobile ? responsiveColumns : finalColumns}
          data={filteredCompanies}
          noHeader
          striped
          highlightOnHover
          pointerOnHover
          dense={isMobile}
          onRowClicked={handleEditCompany}
          conditionalRowStyles={[
            {
              when: (row) => !row.status,
              style: {
                backgroundColor: theme.palette.action.hover,
                opacity: 0.8,
              },
            },
          ]}
        />

        {/* Edit/Add Company Dialog */}
        <EditCompanyForm
          company={selectedCompany}
          isNew={isNewCompany}
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          onSave={handleSaveCompany}
        />
      </Box>
    </>
  );
};

// Main component that wraps the content with the provider
const CompaniesManagementPage: React.FunctionComponent = () => {
  return (
    <CompaniesProvider>
      <CompaniesContent />
    </CompaniesProvider>
  );
};

export default CompaniesManagementPage;
